import React from 'react'
import {
  SyntheticAthlete,
  PhysicalTestRecord,
  getAthletePhysicalTests,
  getAthleteWellnessHistory,
} from './syntheticDashboardData'
import {
  X,
  TrendingDown,
  TrendingUp,
  ArrowRight,
} from 'lucide-react'

interface AthleteFichaDrawerProps {
  athlete: SyntheticAthlete | null
  onClose: () => void
  onNavigateToEvaluations?: () => void
  onNavigateToHealth?: () => void
}

export const AthleteFichaDrawer: React.FC<AthleteFichaDrawerProps> = ({
  athlete,
  onClose,
  onNavigateToEvaluations,
  onNavigateToHealth,
}) => {
  if (!athlete) return null

  const physicalTests = getAthletePhysicalTests()
  const wellnessHistory = getAthleteWellnessHistory()
  const latestWellness = wellnessHistory[wellnessHistory.length - 1]

  const getStatusBadge = () => {
    switch (athlete.availability) {
      case 'available_100':
        return { label: 'Apto 100%', bg: '#dcfce7', text: '#15803d', border: '#bbf7d0' }
      case 'limited':
        return { label: 'Con restricciones', bg: '#fef3c7', text: '#b45309', border: '#fde68a' }
      case 'needs_assessment':
        return { label: 'Pendiente valoración', bg: '#dbeafe', text: '#1d4ed8', border: '#bfdbfe' }
      case 'unavailable':
        return { label: 'No disponible', bg: '#fee2e2', text: '#b91c1c', border: '#fecaca' }
      default:
        return { label: 'Sin valorar', bg: '#f1f5f9', text: '#64748b', border: '#e2e8f0' }
    }
  }

  const statusBadge = getStatusBadge()

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(10, 19, 32, 0.7)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'flex-end',
        backdropFilter: 'blur(3px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '560px',
          height: '100%',
          background: 'var(--surface-card, #ffffff)',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          background: 'var(--nav-bg-base, #0a1320)',
          color: '#ffffff',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '42px',
              height: '42px',
              borderRadius: '8px',
              background: 'var(--brand-red, #b91c1c)',
              color: '#ffffff',
              fontSize: '1.25rem',
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
            }}>
              #{athlete.dorsal}
            </span>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0 }}>
                  {athlete.name}
                </h3>
                <span style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  background: statusBadge.bg,
                  color: statusBadge.text,
                  border: `1px solid ${statusBadge.border}`,
                }}>
                  {statusBadge.label}
                </span>
              </div>

              <div style={{ fontSize: '0.8rem', color: 'var(--nav-text-muted, #94a3b8)', marginTop: '2px' }}>
                {athlete.position} · {athlete.unit} · {athlete.club}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar ficha de atleta"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--nav-text-muted, #94a3b8)',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Drawer Content */}
        <div style={{ padding: '1.25rem 1.5rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          
          {/* Operational Status Box */}
          <div style={{
            background: 'var(--surface-subtle, #f8fafc)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '1rem',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.4rem',
            }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted, #64748b)' }}>
                Estado de Disponibilidad
              </span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #64748b)', fontVariantNumeric: 'tabular-nums' }}>
                Valorado: {athlete.lastAssessmentDate}
              </span>
            </div>

            {athlete.availabilityNote ? (
              <div style={{
                fontSize: '0.82rem',
                color: '#b45309',
                background: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: '6px',
                padding: '8px 10px',
                lineHeight: 1.4,
              }}>
                <strong>Restricción activa:</strong> {athlete.availabilityNote}
                {athlete.nextReviewDate && (
                  <div style={{ marginTop: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                    Próxima revisión: {athlete.nextReviewDate}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: '0.82rem', color: '#15803d', fontWeight: 600 }}>
                ✓ Apto sin restricciones reportadas para sesiones de campo y contacto.
              </div>
            )}
          </div>

          {/* Physical Performance Tests A/B Comparison */}
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.75rem',
            }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                  Evolución y Pruebas Físicas (A/B)
                </h4>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #64748b)' }}>
                  Comparativa: Conc. Marzo vs Conc. Mayo 2026
                </div>
              </div>

              {onNavigateToEvaluations && (
                <button
                  type="button"
                  onClick={onNavigateToEvaluations}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '0.75rem',
                    color: 'var(--brand-red, #b91c1c)',
                    background: 'transparent',
                    border: 'none',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Ver en M8B <ArrowRight size={12} />
                </button>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {physicalTests.map((t: PhysicalTestRecord) => {
                const isImproved = t.direction === 'lower_is_better' ? t.delta < 0 : t.delta > 0
                return (
                  <div
                    key={t.testId}
                    style={{
                      border: '1px solid var(--surface-border, #e2e8f0)',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      background: '#ffffff',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-main, #0f172a)' }}>
                        {t.testName}
                      </span>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 800,
                        color: isImproved ? '#15803d' : '#b91c1c',
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {isImproved ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
                        {t.delta > 0 ? `+${t.delta}` : t.delta} {t.unit} ({t.deltaPercent > 0 ? `+${t.deltaPercent}%` : `${t.deltaPercent}%`})
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      color: 'var(--text-secondary, #475569)',
                      background: 'var(--surface-subtle, #f8fafc)',
                      borderRadius: '4px',
                      padding: '6px 8px',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      <div>
                        Marzo: <strong>{t.sessionA.value} {t.unit}</strong>
                      </div>
                      <div>
                        Mayo: <strong>{t.sessionB.value} {t.unit}</strong>
                      </div>
                      <div style={{ color: 'var(--text-muted, #64748b)' }}>
                        Mediana grupo: {t.teamMedian} {t.unit}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Longitudinal Wellness / Health Profile (Escalas 1-5) */}
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.75rem',
            }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                  Bienestar y Recuperación Semanal
                </h4>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #64748b)' }}>
                  Última respuesta: Semana 8 · Escala clínica 1..5
                </div>
              </div>

              {onNavigateToHealth && (
                <button
                  type="button"
                  onClick={onNavigateToHealth}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '0.75rem',
                    color: 'var(--brand-red, #b91c1c)',
                    background: 'transparent',
                    border: 'none',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Ver en Salud <ArrowRight size={12} />
                </button>
              )}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px',
              textAlign: 'center',
            }}>
              <div style={{
                background: 'var(--surface-subtle, #f8fafc)',
                border: '1px solid var(--surface-border, #e2e8f0)',
                borderRadius: '6px',
                padding: '8px',
              }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted, #64748b)' }}>Sueño medio</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
                  {latestWellness.sleepHours} h
                </div>
                <div style={{ fontSize: '0.68rem', color: '#15803d' }}>Calidad {latestWellness.sleepQuality}/5</div>
              </div>

              <div style={{
                background: 'var(--surface-subtle, #f8fafc)',
                border: '1px solid var(--surface-border, #e2e8f0)',
                borderRadius: '6px',
                padding: '8px',
              }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted, #64748b)' }}>Fatiga</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: latestWellness.fatigue >= 4 ? '#b91c1c' : 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
                  {latestWellness.fatigue}/5
                </div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary, #475569)' }}>Controlada</div>
              </div>

              <div style={{
                background: 'var(--surface-subtle, #f8fafc)',
                border: '1px solid var(--surface-border, #e2e8f0)',
                borderRadius: '6px',
                padding: '8px',
              }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted, #64748b)' }}>Recuperación</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                  {latestWellness.recovery}/5
                </div>
                <div style={{ fontSize: '0.68rem', color: '#15803d' }}>Óptima</div>
              </div>
            </div>
          </div>

          {/* Quick links to original modules */}
          <div style={{
            marginTop: 'auto',
            paddingTop: '1rem',
            borderTop: '1px solid var(--surface-border, #e2e8f0)',
            display: 'flex',
            gap: '8px',
          }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              style={{ flex: 1, fontSize: '0.82rem' }}
            >
              Cerrar ficha
            </button>
            {onNavigateToEvaluations && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={onNavigateToEvaluations}
                style={{ flex: 1, fontSize: '0.82rem' }}
              >
                Abrir en Evaluaciones
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
